import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Heart, MessageCircle, Share2, Trash2, MoreHorizontal, Flag } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';
import type { WPPost } from '../../services/wordpressApi';
import { useAppStore } from '../../stores/wpAppStore';
import Avatar from '../layout/Avatar';
import MatchResultCard from './MatchResultCard';
import ConfirmModal from '../ui/ConfirmModal';
import { wpApi } from '../../services/wordpressApi';

interface PostCardProps {
  post: WPPost;
  onComment?: (post: WPPost) => void;
  onShare?: (post: WPPost) => void;
  onDelete?: (postId: number) => void;
  onReport?: (post: WPPost) => void;
}

const POST_PREVIEW_LENGTH = 280;

function timeAgo(timestamp: number): string {
  try {
    const milliseconds = toMilliseconds(timestamp);
    return formatDistanceToNow(new Date(milliseconds), { addSuffix: true, locale: vi });
  } catch {
    return 'vừa xong';
  }
}

function toMilliseconds(timestamp: number): number {
  return timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp;
}

function getPreviewContent(text: string): string {
  const normalized = text.trim();
  if (normalized.length <= POST_PREVIEW_LENGTH) return normalized;
  return `${normalized.slice(0, POST_PREVIEW_LENGTH).trimEnd()}...`;
}

function renderContent(text: string): React.ReactNode[] {
  const parts = text.split(/(#[\p{L}0-9_-]+|@[\p{L}0-9_-]+)/gu);
  return parts.map((part, index) => {
    if (part.startsWith('#')) {
      return (
        <span key={index} style={{ color: '#3b82f6', fontWeight: 600 }}>
          {part}
        </span>
      );
    }
    if (part.startsWith('@')) {
      return (
        <span key={index} style={{ color: '#f97316', fontWeight: 600 }}>
          {part}
        </span>
      );
    }
    return <span key={index}>{part}</span>;
  });
}

const SUBCATEGORY_LABELS: Record<string, string> = {
  'Snooker': '🎱 Snooker',
  'Pool9': '🎯 Pool 9',
  '3C': '🎯 3 Cushion',
  'Libre': '🎯 Libre',
  'default': '📝 Bài viết',
};

const styles = {
  card: {
    background: '#18181b',
    border: '1px solid #27272a',
    borderRadius: '14px',
    padding: '16px',
    marginBottom: '12px',
    transition: 'all 0.3s ease',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '12px',
  },
  avatar: {
    flexShrink: 0,
    cursor: 'pointer',
  },
  meta: {
    flex: 1,
    minWidth: 0,
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap' as const,
  },
  nameBtn: {
    background: 'none',
    border: 'none',
    color: '#fafafa',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    padding: 0,
    fontFamily: 'inherit',
  },
  dot: {
    color: '#71717a',
    fontSize: '12px',
  },
  time: {
    color: '#71717a',
    fontSize: '13px',
  },
  sublabel: {
    fontSize: '12px',
    color: '#a1a1aa',
    marginTop: '2px',
    display: 'block' as const,
  },
  deleteBtn: {
    background: 'none',
    border: 'none',
    color: '#71717a',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  content: {
    color: '#fafafa',
    fontSize: '15px',
    lineHeight: 1.6,
    marginBottom: '12px',
    wordBreak: 'break-word' as const,
  },
  image: {
    width: '100%',
    borderRadius: '12px',
    marginBottom: '12px',
    display: 'block',
    cursor: 'pointer',
  },
  hashtags: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
    marginBottom: '12px',
  },
  hashtag: {
    padding: '6px 14px',
    background: 'rgba(59, 130, 246, 0.15)',
    border: '1px solid rgba(59, 130, 246, 0.3)',
    borderRadius: '9999px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#60a5fa',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    paddingTop: '8px',
    borderTop: '1px solid #27272a',
  },
  actionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    background: 'none',
    border: 'none',
    color: '#71717a',
    fontSize: '14px',
    cursor: 'pointer',
    borderRadius: '8px',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
  },
  actionBtnLiked: {
    color: '#ef4444',
  },
  reshareBtn: {
    marginLeft: 'auto',
    padding: '8px 16px',
    background: '#27272a',
    border: 'none',
    color: '#fafafa',
    fontSize: '14px',
    fontWeight: 600,
    borderRadius: '9999px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
  },
};

export default function PostCard({ post, onComment, onShare, onDelete, onReport }: PostCardProps) {
  const [imageError, setImageError] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const currentUser = useAppStore((s) => s.currentUser);
  const likePost = useAppStore((s) => s.likePost);
  const unlikePost = useAppStore((s) => s.unlikePost);
  const isLiked = currentUser ? post.likes.includes(currentUser.uid) : false;
  const isOwner = currentUser && currentUser.uid === post.ownerId;
  const previewContent = getPreviewContent(post.content);

  const handleLike = () => {
    if (!currentUser) return;
    if (isLiked) unlikePost(post.id);
    else likePost(post.id);
  };

  const handleShare = async () => {
    const text = [
      post.content,
      post.matchData
        ? `Kết quả: ${post.matchData.players.map((item) => `${item.name} ${item.score}`).join(' - ')}`
        : null,
    ]
      .filter(Boolean)
      .join('\n');

    if (typeof navigator !== 'undefined' && navigator.share) {
      await navigator.share({ title: post.ownerName, text });
      try {
        await wpApi.sharePost(post.id);
      } catch {
        // Keep quiet
      }
      return;
    }
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      toast.success('Đã sao chép nội dung để chia sẻ');
      return;
    }
    onShare?.(post);
  };

  const handleDelete = async () => {
    setShowMenu(false);
    setShowConfirmDelete(true);
  };

  const confirmDelete = () => {
    setShowConfirmDelete(false);
    onDelete?.(post.id);
  };

  const handleReport = () => {
    setShowMenu(false);
    onReport?.(post);
  };

  const handleNavigateUser = (userId: string | number) => {
    window.dispatchEvent(new CustomEvent('vncues:navigate-user', { detail: { userId: String(userId) } }));
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="post-card mb-3 rounded-[14px] border border-zinc-800 bg-zinc-900 p-3.5 transition-all duration-300 sm:p-4"
      layout
    >
      {/* Header */}
      <div className="post-card-header mb-3 flex items-start gap-2.5 sm:gap-3">
        <motion.div className="shrink-0 cursor-pointer">
          <Avatar 
            name={post.ownerName} 
            photoURL={post.ownerPhotoURL} 
            size={48}
            onClick={() => handleNavigateUser(post.ownerId)}
          />
        </motion.div>
        
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <button className="cursor-pointer border-0 bg-transparent p-0 text-[15px] font-semibold text-zinc-50" onClick={() => handleNavigateUser(post.ownerId)}>
              {post.ownerName}
            </button>
            <span className="text-xs text-zinc-500">·</span>
            <time
              className="text-xs text-zinc-500 sm:text-[13px]"
              dateTime={Number.isFinite(Number(post.createdAt)) ? new Date(toMilliseconds(Number(post.createdAt))).toISOString() : undefined}
            >
              {timeAgo(Number(post.createdAt))}
            </time>
          </div>
          <span className="mt-0.5 block text-xs text-zinc-400">
            {post.type === 'match' ? '🏆 Kết quả đấu' : SUBCATEGORY_LABELS[post.subCategory] || SUBCATEGORY_LABELS['default']}
          </span>
        </div>
        
        {currentUser && (
          <div style={{ position: 'relative' }}>
            <motion.button
              className="flex cursor-pointer items-center justify-center rounded-md border-0 bg-transparent p-1 text-zinc-500 transition-all hover:bg-zinc-800 hover:text-zinc-200"
              onClick={() => setShowMenu(!showMenu)}
              whileHover={{ scale: 1.1, background: '#27272a' }}
              whileTap={{ scale: 0.9 }}
              title={isOwner ? 'Tùy chọn' : 'Báo cáo'}
            >
              <MoreHorizontal size={18} />
            </motion.button>
            
            <AnimatePresence>
              {showMenu && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: -10 }}
                  transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute right-0 top-full z-10 mt-2 min-w-[140px] rounded-xl border border-zinc-700 bg-zinc-800 p-2 shadow-2xl"
                >
                  {isOwner ? (
                    <button
                      onClick={handleDelete}
                      className="flex w-full cursor-pointer items-center gap-2 rounded-[10px] border-0 bg-transparent px-3 py-2.5 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/10"
                    >
                      <Trash2 size={16} />
                      Xóa bài
                    </button>
                  ) : (
                    <button
                      onClick={handleReport}
                      className="flex w-full cursor-pointer items-center gap-2 rounded-[10px] border-0 bg-transparent px-3 py-2.5 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/10"
                    >
                      <Flag size={16} />
                      Báo cáo
                    </button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Content text */}
      {post.content && (
        <motion.p 
          className="mb-3 break-words text-[15px] leading-6 text-zinc-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          {renderContent(previewContent)}
        </motion.p>
      )}

      {/* Image */}
      <AnimatePresence>
        {post.imageURL && !imageError && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.3 }}
            className="mb-3 overflow-hidden rounded-xl"
          >
            <motion.img
              src={post.imageURL}
              alt=""
              loading="lazy"
              onError={() => setImageError(true)}
              className="block w-full cursor-pointer rounded-xl"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Match Result */}
      <AnimatePresence>
        {post.matchData && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <MatchResultCard match={post.matchData} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hashtag chips */}
      <AnimatePresence>
        {post.hashtags.length > 0 && (
          <motion.div 
            className="mb-3 flex flex-wrap gap-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
          >
            {post.hashtags.slice(0, 4).map((tag) => (
              <motion.span
                key={tag}
                className="cursor-pointer rounded-full border border-blue-500/30 bg-blue-500/15 px-3.5 py-1.5 text-[13px] font-semibold text-blue-400 transition-all hover:border-blue-500/50 hover:bg-blue-500/30"
                whileHover={{ scale: 1.05, y: -2, background: 'rgba(59, 130, 246, 0.3)', borderColor: 'rgba(59, 130, 246, 0.5)' }}
                whileTap={{ scale: 0.95 }}
              >
                {tag}
              </motion.span>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Action buttons */}
      <div className="post-card-actions flex flex-wrap items-center gap-1 border-t border-zinc-800 pt-2">
        <motion.button
          onClick={handleLike}
          className={`flex min-h-10 cursor-pointer items-center gap-1.5 rounded-lg border-0 bg-transparent px-3 py-2 text-sm transition-all hover:bg-zinc-800 ${isLiked ? 'text-red-500' : 'text-zinc-500'}`}
          whileTap={{ scale: 0.85 }}
          whileHover={{ scale: 1.05, background: '#27272a' }}
        >
          <motion.span
            animate={isLiked ? { scale: [1, 1.3, 1] } : {}}
            transition={{ duration: 0.3 }}
          >
            <Heart size={18} fill={isLiked ? 'currentColor' : 'none'} />
          </motion.span>
          <span>{post.likeCount || ''}</span>
        </motion.button>

        <motion.button
          onClick={() => onComment?.(post)}
          className="flex min-h-10 cursor-pointer items-center gap-1.5 rounded-lg border-0 bg-transparent px-3 py-2 text-sm text-zinc-500 transition-all hover:bg-zinc-800"
          whileTap={{ scale: 0.85 }}
          whileHover={{ scale: 1.05, background: '#27272a' }}
        >
          <MessageCircle size={18} />
          <span>{post.commentCount || ''}</span>
        </motion.button>

        <motion.button
          onClick={handleShare}
          className="flex min-h-10 cursor-pointer items-center gap-1.5 rounded-lg border-0 bg-transparent px-3 py-2 text-sm text-zinc-500 transition-all hover:bg-zinc-800"
          whileTap={{ scale: 0.85 }}
          whileHover={{ scale: 1.05, background: '#27272a' }}
        >
          <Share2 size={18} />
          <span>{post.shareCount || ''}</span>
        </motion.button>
        
        <motion.button
          className="ml-auto cursor-pointer rounded-full border-0 bg-zinc-800 px-4 py-2 text-sm font-semibold text-zinc-50 transition-all hover:bg-zinc-700 max-[600px]:ml-0 max-[600px]:w-full"
          whileHover={{ scale: 1.05, background: '#3f3f46' }}
          whileTap={{ scale: 0.95 }}
        >
          Chia sẻ
        </motion.button>
      </div>

      <ConfirmModal
        open={showConfirmDelete}
        title="Xóa bài viết"
        message="Bạn có chắc muốn xóa bài viết này? Hành động này không thể hoàn tác."
        confirmLabel="Xóa"
        onConfirm={confirmDelete}
        onCancel={() => setShowConfirmDelete(false)}
      />
    </motion.article>
  );
}
